import { segmentsQuery, useDeleteSegmentMutation, type Segment } from "@/api/campaigns";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { seo } from "@/utils/seo";
import { toast } from "@givenwell/components";
import { DotsIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/campaigns/segments/")({
  component: SegmentsPage,
  head: () => ({ meta: seo({ title: "Segments" }) }),
});

function SegmentsPage() {
  const query = useQuery(segmentsQuery());
  const navigate = useNavigate();
  const deleteMut = useDeleteSegmentMutation();

  const rows = query.data || [];

  return (
    <PageWrapper
      title="Segments"
      rightContent={
        <Button
          variant="tonal"
          color="primary"
          onClick={() => navigate({ to: "/campaigns/segments/$segmentId", params: { segmentId: "new" } })}
        >
          New Segment
        </Button>
      }
    >
      <DataTable>
        <DataTableHeader hideToggle />
        <DataTableBody
          data={rows}
          status={query.status}
          dataKey="segment_id"
          columns={[
            {
              header: "Name",
              cell: (segment: Segment) => (
                <StyledLink to="/campaigns/segments/$segmentId" params={{ segmentId: segment.segment_id }}>
                  {segment.name}
                </StyledLink>
              ),
            },
            {
              header: "Description",
              cell: (segment: Segment) => segment.description || "-",
            },
            {
              header: "Created by",
              cell: (segment: Segment) => segment.created_by_name ?? "-",
            },
            {
              header: "Updated by",
              cell: (segment: Segment) => segment.updated_by_name ?? "-",
            },
            {
              header: "Updated",
              cell: (segment: Segment) => new Date(segment.updated_at).toLocaleDateString(),
            },
            {
              header: "",
              css: { align: "right" },
              cell: (segment: Segment) => (
                <DropdownMenu
                  align="end"
                  content={
                    <DropdownMenuItem
                      color="red"
                      onSelect={() => {
                        deleteMut.mutate(segment.segment_id, {
                          onSuccess: () => toast.success("Segment deleted"),
                        });
                      }}
                    >
                      Delete
                    </DropdownMenuItem>
                  }
                >
                  <IconButton aria-label="Actions" variant="plain" size="sm" color="neutral">
                    <DotsIcon style={{ color: colors.text2 }} />
                  </IconButton>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </DataTable>
    </PageWrapper>
  );
}
